import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { downloadBulkPackingSlips } from "@/lib/api";
import { downloadBulkInvoices } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Pill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import DataTable from "@/components/DataTable";
import { DEPT_COLOUR } from "@/lib/departments";
import { formatDate } from "@/lib/datetime";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, RefreshCw, AlertTriangle, ShieldCheck, PlugZap, Printer, FileText, Archive } from "lucide-react";
import { SubscriptionPaymentsPanel } from "@/components/subscriptions/SubscriptionPaymentsPanel";
import { SubscriptionReconPanel } from "@/components/subscriptions/SubscriptionReconPanel";

const money = (v) => (v == null ? "—" : `£${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 })}`);
const SyncLine = ({ label, iso, testid }) => (
  <div className="flex justify-between gap-2 text-xs mt-0.5" data-testid={testid}>
    <span className="text-slate-500 truncate">{label}</span>
    <span className="font-medium text-slate-800 whitespace-nowrap">{iso ? new Date(iso).toLocaleString("en-GB") : "Never"}</span>
  </div>
);


const columns = [
  { key: "reference", label: "Order", mono: true, render: (v, r) => (v ? `#${v}` : r.legacy_id) },
  { key: "created_at", label: "Date", render: (v) => <span className="whitespace-nowrap text-slate-600">{formatDate(v)}</span> },
  { key: "title", label: "Title", strong: true, truncate: true, render: (v, r) => (r.work_type === "Subscription Fulfilment" ? <span title={r.reference}><span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-bold uppercase text-violet-800" data-testid={`sub-fulfil-badge-${r.legacy_id}`}>Subscription Fulfilment</span></span> : v) },
  { key: "public_contact", label: "Customer", truncate: true },
  { key: "company", label: "Company", truncate: true },
  { key: "venue_postcode", label: "Postcode", mono: true },
  { key: "work_instructions", label: "Items", truncate: true },
  {
    key: "current_department", label: "Department",
    render: (v) => (v ? <Pill colour={DEPT_COLOUR[v]}>{v}</Pill> : <span className="text-slate-300">—</span>),
  },
  {
    key: "status", label: "Status",
    render: (v, r) => (v ? <Pill colour={r.status_colour}>{v}</Pill> : <span className="text-slate-300">—</span>),
  },
  { key: "earnings", label: "Total", align: "right", render: (v) => money(v) },
];

export default function WebSalesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [blockMsg, setBlockMsg] = useState(null);
  const [days, setDays] = useState([]);
  const [pickDay, setPickDay] = useState(null);
  const [printing, setPrinting] = useState(false);
  const [invoicing, setInvoicing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [view, setView] = useState("recent");

  const loadStatus = useCallback(() => api.get("/woocommerce/status").then((r) => setStatus(r.data)).catch(() => {}), []);
  const loadDays = useCallback(() => {
    if (!isAdmin) return Promise.resolve();
    return api.get("/web-sales/packing-slips-days").then((r) => {
      const d = r.data.days || [];
      setDays(d);
      if (d.length) setPickDay((prev) => prev || d[0].date);
    }).catch(() => {});
  }, [isAdmin]);

  useEffect(() => { loadStatus(); loadDays(); }, [loadStatus, loadDays]);

  const printPickRun = async () => {
    if (!pickDay) return;
    setPrinting(true);
    try { await downloadBulkPackingSlips(pickDay); toast.success("Pick run downloaded"); }
    catch (e) { toast.error(e.message || "Could not build packing slips"); }
    finally { setPrinting(false); }
  };

  const printInvoices = async () => {
    if (!pickDay) return;
    setInvoicing(true);
    try { await downloadBulkInvoices(pickDay); toast.success("Invoices downloaded"); }
    catch (e) { toast.error(e.message || "Could not build invoices"); }
    finally { setInvoicing(false); }
  };

  const sync = async () => {
    setSyncing(true);
    setBlockMsg(null);
    try {
      const r = await api.post("/woocommerce/sync", null, { params: { pages: 2 } });
      toast.success(`Synced ${r.data.fetched} orders (${r.data.created} new, ${r.data.updated} updated)`);
      await loadStatus();
      await loadDays();
      setRefreshKey((k) => k + 1);
    } catch (e) {
      const detail = e.response?.data?.detail || "Sync failed";
      setBlockMsg(detail);
      toast.error("Could not sync web sales");
    } finally { setSyncing(false); }
  };

  return (
    <div className="px-6 lg:px-8 pt-3 lg:pt-4 pb-6 lg:pb-8" data-testid="web-sales-page">
      <div className="flex items-center justify-between gap-4 mb-3">
        <div className="flex items-center gap-3">
          <img src="/brand/illustrations/web-sales.webp" alt="" className="hidden sm:block w-16 h-auto shrink-0 select-none transition-[transform,filter] duration-300 ease-out hover:-translate-y-1 hover:scale-[1.04] hover:drop-shadow-[0_8px_10px_rgba(15,23,42,0.18)] motion-reduce:transition-none motion-reduce:hover:transform-none" data-testid="web-sales-header-illustration" />
          <div>
            <h1 className="ted-page-title">Web Sales</h1>
            <p className="ted-subtle mt-1">Live WooCommerce orders from thehottubsuperstore.co.uk</p>
          </div>
        </div>
      </div>

      {/* Connection status */}
      <div className="flex flex-col xl:flex-row xl:items-stretch gap-3 mb-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 xl:w-[46%] shrink-0" data-testid="web-sales-status">
        <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
          <PlugZap className={`w-5 h-5 ${status?.configured ? "text-emerald-500" : "text-slate-300"}`} />
          <div><div className="text-[11px] uppercase font-semibold text-slate-400">Connection</div><div className="text-sm font-semibold text-slate-800">{status?.configured ? "Configured" : "Not configured"}</div></div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="text-[11px] uppercase font-semibold text-slate-400">Ready for shipping</div>
          <div className="text-xl font-bold text-slate-900">{status ? (status.ready_to_ship ?? "—") : "—"}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="web-sales-sync-times">
          <div className="text-[11px] uppercase font-semibold text-slate-400">Sync</div>
          <SyncLine label="Fetch OK" iso={status?.last_successful_fetch} testid="web-sales-last-fetch" />
          <SyncLine label="Webhook" iso={status?.last_webhook_at} testid="web-sales-last-webhook" />
          <SyncLine label="Imported" iso={status?.last_sync} testid="web-sales-last-import" />
          {status?.last_imported_order && <div className="text-[11px] text-slate-500 mt-0.5" data-testid="web-sales-newest-order">Newest order {status.last_imported_order}</div>}
        </div>
      </div>

      {isAdmin && days.length > 0 && (
        <div className="flex-1 rounded-xl border border-orange-200 bg-orange-50/60 p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="pick-run-panel">
          <div className="flex items-start gap-3 flex-1">
            <span className="w-9 h-9 rounded-lg bg-white text-orange-600 flex items-center justify-center shrink-0 border border-orange-100"><Printer className="w-4 h-4" /></span>
            <div>
              <div className="text-sm font-bold text-slate-900">Print the day's pick run</div>
              <p className="text-xs text-slate-500 mt-0.5">Combine every web-sale packing slip for a day into one PDF.</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={pickDay || undefined} onValueChange={setPickDay}>
              <SelectTrigger className="h-9 w-52 bg-white" data-testid="pick-run-day-select"><SelectValue placeholder="Select a day" /></SelectTrigger>
              <SelectContent>
                {days.map((d) => (
                  <SelectItem key={d.date} value={d.date}>
                    {new Date(d.date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })} · {d.count} order{d.count === 1 ? "" : "s"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={printPickRun} disabled={printing || !pickDay} className="gap-1.5 bg-orange-600 hover:bg-orange-700 text-white" data-testid="pick-run-download-btn">
              {printing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />} All slips
            </Button>
            <Button onClick={printInvoices} disabled={invoicing || !pickDay} variant="outline" className="gap-1.5 border-orange-300 text-orange-700 hover:bg-orange-100" data-testid="bulk-invoices-download-btn">
              {invoicing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} All invoices
            </Button>
          </div>
        </div>
      )}
      </div>

      {blockMsg && (
        <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3" data-testid="web-sales-block">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-bold text-amber-900">Sync blocked by the store</div>
            <p className="text-sm text-amber-800 mt-0.5">{blockMsg}</p>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 mb-3" data-testid="web-sales-view-tabs">
        <button onClick={() => setView("recent")} data-testid="ws-tab-recent"
          className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold border transition-colors ${view === "recent" ? "bg-orange-600 text-white border-orange-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
          Recent · last 7 days
        </button>
        <button onClick={() => setView("archived")} data-testid="ws-tab-archived"
          className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold border transition-colors ${view === "archived" ? "bg-slate-700 text-white border-slate-700" : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"}`}>
          <Archive className="w-3.5 h-3.5" /> Archived
        </button>
        {isAdmin && [["subpay", "Subscription Payments"], ["recon", "Subscription Reconciliation"]].map(([v, l]) => (
          <button key={v} onClick={() => setView(v)} data-testid={`ws-tab-${v}`}
            className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold border transition-colors ${view === v ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>{l}</button>))}
      </div>

      {view === "subpay" && <SubscriptionPaymentsPanel />}
      {view === "recon" && <SubscriptionReconPanel />}
      {(view === "recent" || view === "archived") && <DataTable
        key={`${refreshKey}-${view}`}
        dataset="tasks"
        title={view === "archived" ? "Archived web sales" : "Web sale jobs · last 7 days"}
        hideHeader
        bare
        columns={columns}
        params={{ web_sales: true, ...(view === "archived" ? { archived: true } : {}) }}
        searchPlaceholder="Search order, customer, postcode, status…"
        rowLink={(r) => `/tasks/${r.legacy_id}`}
      />}
    </div>
  );
}
